import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'

import { useMutation, useQuery } from '@tanstack/react-query'

import { FINITE_ORDER_STATUSES, Order, OrdersUpdateRequest, OrderStatus } from 'src/models/Order'
import { mapTestimonyToOrder } from 'src/models/Testimony'
import { orderingHttpClient, visorHttpClient } from 'src/network'
import { isVisorCustomer } from 'src/network/types'
import { useShallowMemo } from 'src/hooks/useShallowMemo'
import { useTranscriptMetadata } from 'src/contexts/Transcript/MetadataContext'
import { useUserContext } from 'src/contexts/UserProvider'
import { useVerbitConnectAvailability } from 'src/hooks/useVerbitConnectAvailability'
import { useFeatureFlag } from 'src/contexts/FeatureFlagContext'

const ORDER_QUERY_KEY = 'order'
const DEFAULT_POLL_INTERVAL = 15_000
const IN_PROGRESS_POLL_INTERVAL = 120_000

type OrderDetailsContextValue = {
    order: Order | null
    isLoading: boolean
    isOrderPending: boolean
    error: Error | null
    updateOrder: (payload: OrdersUpdateRequest) => Promise<Order>
    isUpdating: boolean
}

const OrderDetailsContext = createContext<OrderDetailsContextValue | null>(null)

type OrderDetailsProviderProps = {
    children: ReactNode
}

export const OrderDetailsProvider = (props: OrderDetailsProviderProps) => {
    const { children } = props
    const metadata = useTranscriptMetadata()
    const { user, customer } = useUserContext()
    const { isMediaRecorderAvailable } = useVerbitConnectAvailability()

    const orderId = metadata?.orderId
    const isDocumentView = metadata?.viewMode === 'document'
    const visorCustomer = isVisorCustomer(customer)

    const [order, setOrder] = useState<Order | null>(null)

    const isFiniteStatus = order !== null && FINITE_ORDER_STATUSES.has(order.status.name)

    const isOrderDetailsPollingOn = useFeatureFlag('order_details_polling')

    const shouldPoll =
        isOrderDetailsPollingOn && !!orderId && !!user && !!customer && isMediaRecorderAvailable && !isDocumentView && !isFiniteStatus

    const refetchInterval = isFiniteStatus
        ? false
        : order?.status.name === OrderStatus.InProgress
          ? IN_PROGRESS_POLL_INTERVAL
          : DEFAULT_POLL_INTERVAL

    const { data, isLoading, error } = useQuery<Order, Error>({
        queryKey: [ORDER_QUERY_KEY, orderId],
        queryFn: () =>
            visorCustomer ? visorHttpClient.getTestimony(orderId!).then(mapTestimonyToOrder) : orderingHttpClient.getOrder(orderId!),
        enabled: shouldPoll,
        refetchInterval,
        refetchOnWindowFocus: false,
    })

    const isOrderPending = shouldPoll && !order && !error

    useEffect(() => {
        if (data) {
            setOrder(data)
        }
    }, [data])

    const { mutateAsync, isPending: isUpdating } = useMutation<Order, Error, OrdersUpdateRequest>({
        mutationFn: (payload) => visorHttpClient.updateTestimony(orderId!, payload).then(mapTestimonyToOrder),
        onSuccess: (updatedOrder) => {
            setOrder(updatedOrder)
        },
    })

    const noopUpdate = useMemo(() => () => Promise.resolve(order!), [order])

    const updateOrder = useMemo(
        () => (visorCustomer ? (payload: OrdersUpdateRequest) => mutateAsync(payload) : noopUpdate),
        [visorCustomer, mutateAsync, noopUpdate],
    )

    return (
        <OrderDetailsContext.Provider
            value={useShallowMemo({
                order,
                isLoading,
                isOrderPending,
                error,
                updateOrder,
                isUpdating,
            })}>
            {children}
        </OrderDetailsContext.Provider>
    )
}

export const useOrderDetails = () => {
    const contextValue = useContext(OrderDetailsContext)

    if (contextValue === null) {
        throw new Error('You have forgot to use <OrderDetailsContext.Provider />, shame on you.')
    }

    return contextValue
}
